import { cn } from '@/lib/cn';
import { renderMarkdown } from '@/lib/markdown';
import type { Editor } from '@tiptap/core';
import {
  For,
  type JSX,
  Show,
  createEffect,
  createSignal,
  createUniqueId,
  onCleanup,
  onMount,
} from 'solid-js';

export interface MarkdownEditorProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  /** Taller editing area, e.g. inside a large dialog. */
  tall?: boolean;
}

type Prompt = 'link' | 'image';

// [label, aria label, run, active check]
const ACTIONS: readonly [string, string, (e: Editor) => void, (e: Editor) => boolean][] = [
  ['B', 'Bold', (e) => e.chain().focus().toggleBold().run(), (e) => e.isActive('bold')],
  ['I', 'Italic', (e) => e.chain().focus().toggleItalic().run(), (e) => e.isActive('italic')],
  [
    'H',
    'Heading',
    (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
    (e) => e.isActive('heading'),
  ],
  [
    '•',
    'Bulleted list',
    (e) => e.chain().focus().toggleBulletList().run(),
    (e) => e.isActive('bulletList'),
  ],
  [
    '1.',
    'Numbered list',
    (e) => e.chain().focus().toggleOrderedList().run(),
    (e) => e.isActive('orderedList'),
  ],
  ['`c`', 'Inline code', (e) => e.chain().focus().toggleCode().run(), (e) => e.isActive('code')],
];

const BUTTON =
  'min-w-7 rounded-md px-1.5 py-1 text-xs font-semibold text-neutral-600 transition-colors hover:bg-neutral-100 motion-reduce:transition-none disabled:opacity-40 dark:text-neutral-300 dark:hover:bg-neutral-800';
const PROMPT_INPUT =
  'min-w-0 flex-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm outline-none focus:border-brand-500 dark:border-neutral-700 dark:bg-neutral-900';
const PRESSED = 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200';

/**
 * WYSIWYG markdown editor (Tiptap): formatting renders as you type and the
 * value stays markdown. "View code" swaps in the raw markdown textarea.
 */
export const MarkdownEditor = (props: MarkdownEditorProps): JSX.Element => {
  const id = `md-${createUniqueId()}`;
  const [code, setCode] = createSignal(false);
  const [editor, setEditor] = createSignal<Editor>();
  // Bumped on every editor transaction so toolbar pressed-states re-read isActive().
  const [tick, setTick] = createSignal(0);
  const [prompt, setPrompt] = createSignal<Prompt | null>(null);
  const [url, setUrl] = createSignal('');
  const [alt, setAlt] = createSignal('');
  // True when the visual editor can't round-trip the markdown (e.g. tables) — edit as code instead.
  const [lossy, setLossy] = createSignal(false);
  let host: HTMLDivElement | undefined;
  let disposed = false;

  // Tiptap loads on first use so pages without an editor don't ship it.
  onMount(async () => {
    const [{ Editor }, { default: Image }, { Placeholder }, { Markdown }, { default: StarterKit }] =
      await Promise.all([
        import('@tiptap/core'),
        import('@tiptap/extension-image'),
        import('@tiptap/extensions'),
        import('@tiptap/markdown'),
        import('@tiptap/starter-kit'),
      ]);
    if (disposed) return;
    const instance = new Editor({
      element: host,
      extensions: [
        // Underline would serialize to raw <u>, which renderMarkdown escapes.
        StarterKit.configure({ link: { openOnClick: false }, underline: false }),
        Image,
        Markdown,
        Placeholder.configure({ placeholder: props.placeholder ?? '' }),
      ],
      content: props.value,
      contentType: 'markdown',
      editorProps: {
        attributes: {
          id,
          role: 'textbox',
          'aria-multiline': 'true',
          ...(props.label ? { 'aria-label': props.label } : {}),
          class: cn(
            'markdown px-3 py-2 text-sm text-neutral-900 outline-none dark:text-neutral-100',
            props.tall ? 'min-h-80' : 'min-h-32',
          ),
        },
      },
      onUpdate: ({ editor: e }) => props.onChange(e.getMarkdown()),
      onTransaction: () => setTick((n) => n + 1),
    });
    setEditor(instance);
  });
  onCleanup(() => {
    disposed = true;
    editor()?.destroy();
  });

  // Outside changes (initial load, reset, code view edits) flow back into the editor. If the
  // editor would drop anything the renderer shows, stay in code view so nothing is lost.
  createEffect(() => {
    const instance = editor();
    if (!instance || code() || instance.getMarkdown() === props.value) return;
    instance.commands.setContent(props.value, { contentType: 'markdown', emitUpdate: false });
    const dropsContent = renderMarkdown(instance.getMarkdown()) !== renderMarkdown(props.value);
    setLossy(dropsContent);
    if (dropsContent) setCode(true);
  });

  const active = (check: (e: Editor) => boolean) => {
    tick();
    const instance = editor();
    return Boolean(instance && !code() && check(instance));
  };

  const openPrompt = (kind: Prompt) => {
    setUrl(kind === 'link' ? ((editor()?.getAttributes('link').href as string) ?? '') : '');
    setAlt('');
    setPrompt(prompt() === kind ? null : kind);
  };

  const applyPrompt = () => {
    const instance = editor();
    // Tiptap writes attributes into markdown unescaped: keep `[]` and `\` out of alt text and
    // percent-encode `()`/spaces in URLs so the saved image/link syntax stays valid.
    const href = url()
      .trim()
      .replace(/[()\s]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);
    if (!instance) return;
    if (prompt() === 'link') {
      const chain = instance.chain().focus().extendMarkRange('link');
      (href ? chain.setLink({ href }) : chain.unsetLink()).run();
    } else if (href) {
      instance
        .chain()
        .focus()
        .setImage({
          src: href,
          alt: alt()
            .replace(/[[\]\\]/g, '')
            .trim(),
        })
        .run();
    }
    setPrompt(null);
  };

  // Keep Enter/Escape inside the URL box: no parent form submit, no dialog close, no IME clash.
  const onPromptKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') e.stopPropagation();
    if (e.isComposing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      applyPrompt();
    } else if (e.key === 'Escape') {
      setPrompt(null);
      editor()?.commands.focus();
    }
  };

  return (
    <div class="flex flex-col gap-1 text-sm">
      <Show when={props.label}>
        <label
          for={id}
          class={cn(
            'font-medium text-neutral-700 dark:text-neutral-300',
            props.required && "after:ml-0.5 after:text-red-500 after:content-['*']",
          )}
        >
          {props.label}
        </label>
      </Show>
      <div class="rounded-lg border border-neutral-300 bg-white transition focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500 motion-reduce:transition-none dark:border-neutral-700 dark:bg-neutral-900">
        <div
          class="flex flex-wrap items-center gap-0.5 border-b border-neutral-200 px-2 py-1 dark:border-neutral-800"
          role="toolbar"
          aria-label="Formatting"
        >
          <For each={ACTIONS}>
            {([text, aria, run, check]) => (
              <button
                type="button"
                aria-label={aria}
                title={aria}
                aria-pressed={active(check)}
                disabled={code()}
                onClick={() => {
                  const instance = editor();
                  if (instance) run(instance);
                }}
                class={cn(BUTTON, active(check) && PRESSED)}
              >
                {text}
              </button>
            )}
          </For>
          <button
            type="button"
            aria-label="Link"
            title="Link"
            aria-pressed={active((e) => e.isActive('link'))}
            disabled={code()}
            onClick={() => openPrompt('link')}
            class={cn(
              BUTTON,
              (active((e) => e.isActive('link')) || prompt() === 'link') && PRESSED,
            )}
          >
            Link
          </button>
          <button
            type="button"
            aria-label="Image from URL"
            title="Image from URL"
            disabled={code()}
            onClick={() => openPrompt('image')}
            class={cn(BUTTON, prompt() === 'image' && PRESSED)}
          >
            Image
          </button>
          <button
            type="button"
            aria-pressed={code()}
            onClick={() => {
              setPrompt(null);
              setCode(!code());
            }}
            class={cn(BUTTON, 'ml-auto font-mono', code() && PRESSED)}
          >
            {'</>'} View code
          </button>
        </div>
        <div
          class={cn(
            'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
            prompt() ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div class="overflow-hidden" inert={!prompt() || undefined}>
            <div class="flex gap-2 border-b border-neutral-200 px-2 py-2 dark:border-neutral-800">
              <input
                type="text"
                inputMode="url"
                aria-label={prompt() === 'image' ? 'Image URL' : 'Link URL'}
                placeholder="https://"
                value={url()}
                onInput={(e) => setUrl(e.currentTarget.value)}
                onKeyDown={onPromptKey}
                class={PROMPT_INPUT}
              />
              <Show when={prompt() === 'image'}>
                <input
                  type="text"
                  aria-label="Image description (alt text)"
                  placeholder="Describe the image"
                  value={alt()}
                  onInput={(e) => setAlt(e.currentTarget.value)}
                  onKeyDown={onPromptKey}
                  class={PROMPT_INPUT}
                />
              </Show>
              <button
                type="button"
                onClick={applyPrompt}
                class={cn(BUTTON, 'bg-brand-600 px-3 text-white hover:bg-brand-700')}
              >
                {prompt() === 'image' ? 'Insert' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
        <div ref={host} hidden={code()} class={props.tall ? 'min-h-80' : 'min-h-32'} />
        <Show when={code()}>
          <textarea
            aria-label={props.label ? `${props.label} (markdown)` : 'Markdown'}
            required={props.required}
            placeholder={props.placeholder}
            value={props.value}
            onInput={(e) => props.onChange(e.currentTarget.value)}
            class={cn(
              'block w-full resize-y rounded-b-lg bg-transparent px-3 py-2 font-mono text-sm text-neutral-900 outline-none dark:text-neutral-100',
              props.tall ? 'min-h-80' : 'min-h-32',
            )}
          />
        </Show>
      </div>
      <Show when={lossy() && code()}>
        <span class="text-xs text-amber-700 dark:text-amber-300">
          This content uses formatting the visual editor can't keep (such as tables), so it opens as
          code.
        </span>
      </Show>
      <span class="text-xs text-neutral-500">
        Saved as Markdown · use View code to edit it directly
      </span>
    </div>
  );
};
